import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Package, User, Globe, FileText, Save, Search, Plus, Printer } from 'lucide-react';
import apiClient from '../services/api';

const AMBER      = '#f59e0b';
const AMBER_DARK = '#d97706';
const CONSIGNMENT_TYPE = 'I';

const ConsignmentEntryFormI = ({ onClose, onSave, editData = null }) => {
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [employees,        setEmployees]        = useState([]);
  const [customers,        setCustomers]        = useState([]);
  const [countries,        setCountries]        = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false);

  const [showCustomerModal,   setShowCustomerModal]   = useState(false);
  const [customerModalType,   setCustomerModalType]   = useState('');
  const [customerSearchTerm,  setCustomerSearchTerm]  = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session   = getSessionData();
  const companyId = session?.companyid;

  const fixDateTime = (val) => {
    if (!val || val === '0000-00-00 00:00:00' || val === '0000-00-00') return '';
    if (val.includes('T')) return val.slice(0, 16);
    if (val.includes(' ') && val.length > 10) return val.replace(' ', 'T').slice(0, 16);
    return val.slice(0, 10) + 'T00:00';
  };
  const fixDate = (val) => (!val || val === '0000-00-00') ? '' : val.slice(0, 10);

  const emptyForm = {
    ConsignmentNo: '',
    ConsignmentDate: (() => { const now = new Date(); return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })(),
    ConsignmentType: CONSIGNMENT_TYPE,
    ShipperID: '', ShipperCode: '', ShipperName: '', ShipperContactPerson: '',
    ShipperAddress: '', ShipperPhone: '', ShipperCountry: '', ShipperCity: '', ShipperPostalCode: '',
    ConsigneeID: '', ConsigneeCode: '', ConsigneeName: '', ConsigneeContactPerson: '',
    ConsigneeAddress: '', ConsigneePhone: '', ConsigneeCountry: '', ConsigneeCity: '', ConsigneePostalCode: '',
    Description: '', NoOfPkgs: '', Weight: '', Pieces: '',
    InvoiceValue: '', CurrencyCode: 'USD', AWBNumber: '', FlightNumber: '',
    CustomsValue: '', HSCode: '', IncoTerms: 'FOB',
    CourierStatusID: '', DeliveryDate: '', CollectedBy: '', ReceivedBy: '', Remarks: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [newCustomerData, setNewCustomerData] = useState({ customername: '', customertype: 'CR', phone: '', email: '', address: '', cityid: '', taxnumber: '' });

  const incoTermsOptions = ['FOB','CIF','EXW','FCA','CPT','CIP','DAP','DPU','DDP'];
  const currencyOptions  = ['USD','EUR','GBP','AED','SAR','INR','JPY','CNY'];

  useEffect(() => {
    const init = async () => {
      if (editData?.ConsignmentID) {
        const ed = editData;
        setFormData({
          ConsignmentNo: ed.ConsignmentNo || '', ConsignmentDate: fixDateTime(ed.ConsignmentDate),
          ConsignmentType: CONSIGNMENT_TYPE,
          ShipperID: String(ed.ShipperID || ed.ConsignorID || ''), ShipperCode: ed.ShipperCode || '',
          ShipperName: ed.ShipperName || ed.Shipper || '',
          ShipperContactPerson: ed.ShipperContactPerson || ed.ShipperContactName || '',
          ShipperAddress: ed.ShipperAddress || ed.ShipperLocation || '',
          ShipperPhone: ed.ShipperPhone || '', ShipperCountry: ed.ShipperCountry || '',
          ShipperCity: ed.ShipperCity || '', ShipperPostalCode: ed.ShipperPostalCode || ed.ShipperPOBox || '',
          ConsigneeID: String(ed.ConsigneeID || ''), ConsigneeCode: ed.ConsigneeCode || '',
          ConsigneeName: ed.ConsigneeName || ed.Consignee || '',
          ConsigneeContactPerson: ed.ConsigneeContactPerson || ed.ConsigneeContactName || '',
          ConsigneeAddress: ed.ConsigneeAddress || ed.ConsigneeLocation || '',
          ConsigneePhone: ed.ConsigneePhone || '', ConsigneeCountry: ed.ConsigneeCountry || '',
          ConsigneeCity: ed.ConsigneeCity || '', ConsigneePostalCode: ed.ConsigneePostalCode || ed.ConsigneePOBox || '',
          Description: ed.Description || '', NoOfPkgs: ed.NoOfPkgs || '', Weight: ed.Weight || '',
          Pieces: ed.Pieces || '', InvoiceValue: ed.InvoiceValue || '', CurrencyCode: ed.CurrencyCode || 'USD',
          AWBNumber: ed.AWBNumber || '', FlightNumber: ed.FlightNumber || '',
          CustomsValue: ed.CustomsValue || '', HSCode: ed.HSCode || '', IncoTerms: ed.IncoTerms || 'FOB',
          CourierStatusID: String(ed.CourierStatusID || ''), DeliveryDate: fixDate(ed.DeliveryDate),
          CollectedBy: String(ed.CollectedBy || ''), ReceivedBy: String(ed.ReceivedBy || ''),
          Remarks: ed.Remarks || '',
        });
        loadDropdownData();
      } else {
        await loadDropdownData();
        setGeneratingNumber(true);
        await generateConsignmentNumber();
        setGeneratingNumber(false);
      }
    };
    init();
  }, [editData]);

  const loadDropdownData = async () => {
    try {
      const [empRes, custRes, countryRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Customers.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Countries.php').catch(() => ({ data: { success: false } })),
      ]);
      if (empRes.data.success)     setEmployees(empRes.data.data || []);
      if (custRes.data.success)    setCustomers(custRes.data.data || []);
      if (countryRes.data.success) setCountries(countryRes.data.data || []);
    } catch (err) { console.error(err); setError('Failed to load dropdown data'); }
  };

  const generateConsignmentNumber = async () => {
    try {
      const res = await apiClient.get(`/ConsignmentsI.php/next-number?type=${CONSIGNMENT_TYPE}`);
      if (res.data.success && res.data.data?.consignmentNo) {
        setFormData(p => ({ ...p, ConsignmentNo: res.data.data.consignmentNo }));
      } else throw new Error('Invalid response');
    } catch {
      const fb = `CNS-I-${Date.now()}`;
      setFormData(p => ({ ...p, ConsignmentNo: fb }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCustomerModal = (type) => {
    setCustomerModalType(type); setCustomerSearchTerm('');
    setShowAddCustomerForm(false); setShowCustomerModal(true);
  };

  const selectCustomer = (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(p => ({ ...p, ShipperID: customer.customerid, ShipperCode: customer.customercode, ShipperName: customer.customername, ShipperPhone: customer.phone || '', ShipperAddress: customer.address || '' }));
    } else {
      setFormData(p => ({ ...p, ConsigneeID: customer.customerid, ConsigneeCode: customer.customercode, ConsigneeName: customer.customername, ConsigneePhone: customer.phone || '', ConsigneeAddress: customer.address || '' }));
    }
    setShowCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!newCustomerData.customername.trim()) { alert('Customer name is required'); return; }
    try {
      const res = await apiClient.post('/Customers.php', { ...newCustomerData, companyid: companyId, isactive: 1 });
      if (res.data.success) {
        await loadDropdownData();
        selectCustomer({ customerid: res.data.data.customerid, customercode: res.data.data.customercode, customername: newCustomerData.customername, phone: newCustomerData.phone, address: newCustomerData.address });
        setNewCustomerData({ customername: '', customertype: 'CR', phone: '', email: '', address: '', cityid: '', taxnumber: '' });
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${res.data.data.customercode}`);
      } else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error adding customer: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ConsignmentNo.trim()) { setError('Consignment number is required'); return; }
    if (!formData.ShipperName.trim())   { setError('Shipper name is required');       return; }
    if (!formData.ConsigneeName.trim()) { setError('Consignee name is required');     return; }
    setLoading(true); setError('');
    try {
      const dataToSave = { ...formData, ConsignmentType: CONSIGNMENT_TYPE, ShipperID: formData.ShipperID || null, ConsigneeID: formData.ConsigneeID || null, CollectedBy: formData.CollectedBy || null, ReceivedBy: formData.ReceivedBy || null, CourierStatusID: formData.CourierStatusID || null, DeliveryDate: formData.DeliveryDate || null, NoOfPkgs: formData.NoOfPkgs || 0, Weight: formData.Weight || 0, Pieces: formData.Pieces || 0, InvoiceValue: formData.InvoiceValue || 0, CustomsValue: formData.CustomsValue || 0 };
      if (editData?.ConsignmentID) dataToSave.ConsignmentID = editData.ConsignmentID;
      await onSave(dataToSave);
    } catch (err) { setError(err.message || 'Failed to save consignment'); }
    finally { setLoading(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  const inpCls = `w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-amber-400 focus:shadow-[0_0_0_3px_rgba(245,158,11,0.12)] font-[inherit]`;
  const numCls = inpCls + ' text-right font-mono font-semibold tracking-wide';
  const selCls = inpCls + ' cursor-pointer';
  const lblCls = 'block mb-1.5 text-sm font-medium text-gray-700';

  const SectionTitle = ({ icon: Icon, title, action }) => (
    <div className="flex items-center gap-2.5 mb-4 pb-3 border-b-2 border-gray-100">
      <Icon size={16} className="text-amber-500 flex-shrink-0" />
      <span className="text-[15px] font-semibold text-gray-800 flex-1">{title}</span>
      {action}
    </div>
  );

  const FormSection = ({ children }) => (
    <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">{children}</div>
  );

  const SelectBtn = ({ type }) => (
    <button type="button" onClick={() => openCustomerModal(type)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
      style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
      <Search size={12} /> Select
    </button>
  );

  const customerModalJSX = showCustomerModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}>
      <div className="bg-white rounded-xl w-full max-w-[800px] max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
          <h3 className="text-lg font-semibold m-0" style={{ color: '#fff' }}>
            Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}
          </h3>
          <button onClick={() => setShowCustomerModal(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
            style={{ color: '#fff' }}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center gap-2 px-3 py-2.5 border-2 border-gray-300 rounded-lg bg-white mb-4 focus-within:border-amber-400 transition-colors duration-150">
            <Search size={18} className="text-gray-400" />
            <input type="text" placeholder="Search customers..." value={customerSearchTerm}
              onChange={e => setCustomerSearchTerm(e.target.value)} autoFocus
              className="flex-1 border-0 outline-none text-sm" />
          </div>
          <button type="button" onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
            className="flex items-center gap-1.5 px-3 py-2 mb-4 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
            style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
            <Plus size={14} /> Add New Customer
          </button>
          {showAddCustomerForm && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-bold text-gray-700 mb-3">New Customer</h4>
              <form onSubmit={handleAddCustomer}>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lblCls}>Name *</label><input type="text" value={newCustomerData.customername} onChange={e => setNewCustomerData({ ...newCustomerData, customername: e.target.value })} required className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" value={newCustomerData.phone} onChange={e => setNewCustomerData({ ...newCustomerData, phone: e.target.value })} className={inpCls} /></div>
                  <div><label className={lblCls}>Email</label><input type="email" value={newCustomerData.email} onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })} className={inpCls} /></div>
                  <div><label className={lblCls}>Address</label><input type="text" value={newCustomerData.address} onChange={e => setNewCustomerData({ ...newCustomerData, address: e.target.value })} className={inpCls} /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="submit" className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer" style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>Create & Select</button>
                  <button type="button" onClick={() => setShowAddCustomerForm(false)} className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          )}
          <div className="max-h-[380px] overflow-y-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>{['Code','Name','Phone','Action'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-gray-200">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0
                  ? <tr><td colSpan="4" className="py-8 text-center text-gray-400 text-sm">No customers found</td></tr>
                  : filteredCustomers.map(c => (
                    <tr key={c.customerid} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{c.customercode}</td>
                      <td className="px-3 py-2.5 font-medium text-gray-800">{c.customername}</td>
                      <td className="px-3 py-2.5 text-gray-500">{c.phone || '-'}</td>
                      <td className="px-3 py-2.5">
                        <button type="button" onClick={() => selectCustomer(c)}
                          className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
                          style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (!companyId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[9999]">
        <div className="bg-white rounded-xl w-full max-w-[500px] p-8 text-center shadow-2xl">
          <p className="text-gray-600 mb-4">Please select a company and financial year from the dashboard.</p>
          <button onClick={onClose} className="px-4 py-2 text-white border-0 rounded-lg cursor-pointer" style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 overflow-y-auto" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl w-full max-w-[1200px] max-h-[95vh] flex flex-col shadow-2xl" style={{ animation: 'slideIn 0.3s ease-out' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 rounded-t-xl flex-shrink-0 border-b-4"
            style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, borderBottomColor: AMBER_DARK }}>
            <div className="flex items-center gap-6 flex-1">
              <div className="w-14 h-14 rounded-xl bg-white/30 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Globe size={24} color="#fff" />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[1.6rem] font-bold" style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
                  {editData ? 'Edit' : 'New'} Consignment – International (I)
                </span>
                <span className="font-mono text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/40 bg-white/25 whitespace-nowrap" style={{ color: '#fff' }}>
                  📋 {generatingNumber ? 'Generating...' : (formData.ConsignmentNo || 'Loading...')}
                </span>
              </div>
            </div>
            <button onClick={onClose}
              className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-white/30 bg-white/20 cursor-pointer transition-all duration-150 hover:bg-white/35 hover:rotate-90 flex-shrink-0"
              style={{ color: '#fff' }}>
              <X size={20} color="#fff" />
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-6 py-3 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium flex-shrink-0">
              <strong>Error:</strong> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50">

              {/* Basic Info */}
              <FormSection>
                <SectionTitle icon={FileText} title="Basic Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div>
                    <label className={lblCls}>Consignment No *</label>
                    <input type="text" name="ConsignmentNo" value={generatingNumber ? 'Loading...' : formData.ConsignmentNo} readOnly
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm font-bold text-[1.05rem] outline-none ${generatingNumber ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-emerald-50 text-gray-900 border-emerald-300'}`} />
                  </div>
                  <div>
                    <label className={lblCls}>Consignment Date & Time *</label>
                    <input type="datetime-local" name="ConsignmentDate" value={formData.ConsignmentDate} onChange={handleChange} required className={inpCls} />
                  </div>
                  <div>
                    <label className={lblCls}>AWB Number</label>
                    <input type="text" name="AWBNumber" value={formData.AWBNumber} onChange={handleChange} placeholder="Air Waybill Number" className={inpCls} />
                  </div>
                  <div>
                    <label className={lblCls}>Flight Number</label>
                    <input type="text" name="FlightNumber" value={formData.FlightNumber} onChange={handleChange} placeholder="e.g., EK123" className={inpCls} />
                  </div>
                </div>
              </FormSection>

              {/* Shipper */}
              <FormSection>
                <SectionTitle icon={User} title="Shipper | Sender" action={<SelectBtn type="shipper" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" value={formData.ShipperCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ShipperName" value={formData.ShipperName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ShipperContactPerson" value={formData.ShipperContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ShipperPhone" value={formData.ShipperPhone} onChange={handleChange} className={inpCls} /></div>
                  <div className="col-span-full"><label className={lblCls}>Address</label><textarea name="ShipperAddress" value={formData.ShipperAddress} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                  <div><label className={lblCls}>Country</label>
                    <select name="ShipperCountry" value={formData.ShipperCountry} onChange={handleChange} className={selCls}>
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c.countryid} value={c.countryname}>{c.countryname}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>City</label><input type="text" name="ShipperCity" value={formData.ShipperCity} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Postal Code</label><input type="text" name="ShipperPostalCode" value={formData.ShipperPostalCode} onChange={handleChange} className={inpCls} /></div>
                </div>
              </FormSection>

              {/* Consignee */}
              <FormSection>
                <SectionTitle icon={User} title="Consignee | Receiver" action={<SelectBtn type="consignee" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" value={formData.ConsigneeCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ConsigneeName" value={formData.ConsigneeName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ConsigneeContactPerson" value={formData.ConsigneeContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ConsigneePhone" value={formData.ConsigneePhone} onChange={handleChange} className={inpCls} /></div>
                  <div className="col-span-full"><label className={lblCls}>Address</label><textarea name="ConsigneeAddress" value={formData.ConsigneeAddress} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                  <div><label className={lblCls}>Country</label>
                    <select name="ConsigneeCountry" value={formData.ConsigneeCountry} onChange={handleChange} className={selCls}>
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c.countryid} value={c.countryname}>{c.countryname}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>City</label><input type="text" name="ConsigneeCity" value={formData.ConsigneeCity} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Postal Code</label><input type="text" name="ConsigneePostalCode" value={formData.ConsigneePostalCode} onChange={handleChange} className={inpCls} /></div>
                </div>
              </FormSection>

              {/* Package & Customs */}
              <FormSection>
                <SectionTitle icon={Package} title="Package & Customs Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div className="col-span-full"><label className={lblCls}>Description</label><textarea name="Description" value={formData.Description} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                  <div><label className={lblCls}>No. of Packages</label><input type="number" name="NoOfPkgs" value={formData.NoOfPkgs} onChange={handleChange} min="0" step="1" className={numCls} /></div>
                  <div><label className={lblCls}>Pieces</label><input type="number" name="Pieces" value={formData.Pieces} onChange={handleChange} min="0" step="1" className={numCls} /></div>
                  <div><label className={lblCls}>Weight (kg)</label><input type="number" name="Weight" value={formData.Weight} onChange={handleChange} min="0" step="0.001" className={numCls} /></div>
                  <div><label className={lblCls}>Invoice Value</label><input type="number" name="InvoiceValue" value={formData.InvoiceValue} onChange={handleChange} min="0" step="0.01" className={numCls} /></div>
                  <div><label className={lblCls}>Customs Value</label><input type="number" name="CustomsValue" value={formData.CustomsValue} onChange={handleChange} min="0" step="0.01" className={numCls} /></div>
                  <div><label className={lblCls}>Currency</label>
                    <select name="CurrencyCode" value={formData.CurrencyCode} onChange={handleChange} className={selCls}>
                      {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>HS Code</label><input type="text" name="HSCode" value={formData.HSCode} onChange={handleChange} placeholder="Harmonized System Code" className={inpCls} /></div>
                  <div><label className={lblCls}>IncoTerms</label>
                    <select name="IncoTerms" value={formData.IncoTerms} onChange={handleChange} className={selCls}>
                      {incoTermsOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Delivery */}
              <FormSection>
                <SectionTitle icon={FileText} title="Delivery Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div><label className={lblCls}>Collected By</label>
                    <select name="CollectedBy" value={formData.CollectedBy} onChange={handleChange} className={selCls}>
                      <option value="">Select Employee</option>
                      {employees.map(e => <option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Received By</label>
                    <select name="ReceivedBy" value={formData.ReceivedBy} onChange={handleChange} className={selCls}>
                      <option value="">Select Employee</option>
                      {employees.map(e => <option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                    </select>
                  </div>
                  <div><label className={lblCls}>Status</label>
                    <select name="CourierStatusID" value={formData.CourierStatusID} onChange={handleChange} className={selCls}>
                      <option value="">Select Status</option>
                      <option value="1">Pending</option><option value="2">In Transit</option>
                      <option value="3">Delivered</option><option value="4">Returned</option>
                    </select>
                  </div>
                  <div><label className={lblCls}>Delivery Date</label><input type="date" name="DeliveryDate" value={formData.DeliveryDate} onChange={handleChange} className={inpCls} /></div>
                  <div className="col-span-full"><label className={lblCls}>Remarks</label><textarea name="Remarks" value={formData.Remarks} onChange={handleChange} rows={2} className={`${inpCls} resize-y min-h-[60px]`} /></div>
                </div>
              </FormSection>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex-shrink-0">
              <button type="button" onClick={onClose} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-600 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150">
                Cancel
              </button>
              {editData && (
                <button type="button" disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
                  style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
                  <Printer size={16} /> Print Consignment
                </button>
              )}
              <button type="submit" disabled={loading || generatingNumber}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(245,158,11,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all duration-150"
                style={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` }}>
                {loading
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                  : <><Save size={16} />Save International Consignment</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {customerModalJSX}
      <style>{`@keyframes slideIn { from { opacity:0; transform:translateY(-20px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </>
  );
};

export default ConsignmentEntryFormI;